import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, orderBy, limit } from 'firebase/firestore';
import { db } from '../firebase/config';
import { Line } from 'react-chartjs-2';
import { Flame } from 'lucide-react';

interface EmissionsDisplayProps {
  netuid: string | number;
}

interface EmissionsEntry {
  block_number: number;
  timestamp: string;
  emissions: number;
  emissions_24h: number;
  avg_emissions_per_block: number;
}

export function EmissionsDisplay({ netuid }: EmissionsDisplayProps) {
  const [emissionsData, setEmissionsData] = useState<EmissionsEntry | null>(null);
  const [emissionsHistory, setEmissionsHistory] = useState<EmissionsEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchEmissionsData = async () => {
      try {
        setLoading(true);
        
        // Get the emissions history from the subcollection
        const entriesRef = collection(db, 'emissions', netuid.toString(), 'entries');
        const entriesQuery = query(entriesRef, orderBy('block_number', 'desc'), limit(20));
        const entriesSnapshot = await getDocs(entriesQuery);
        
        if (!entriesSnapshot.empty) {
          // Get latest entry
          const latestData = entriesSnapshot.docs[0].data() as EmissionsEntry;
          setEmissionsData(latestData);

          // Get history data
          const historyData = entriesSnapshot.docs.map(doc => {
            const data = doc.data();
            return {
              block_number: data.block_number,
              timestamp: data.timestamp,
              emissions: data.emissions || 0,
              emissions_24h: data.emissions_24h || 0,
              avg_emissions_per_block: data.avg_emissions_per_block || 0
            };
          });
          setEmissionsHistory(historyData.reverse()); // Reverse to get chronological order
        } else {
          setError('No emissions data available');
        }
      } catch (error) {
        console.error('Error fetching emissions data:', error);
        setError('Failed to load emissions data');
      } finally {
        setLoading(false);
      }
    };

    if (netuid) {
      fetchEmissionsData();
    }
  }, [netuid]);

  // Format emissions value as percentage
  const formatEmissions = (value: number): string => {
    return value.toFixed(2) + '%';
  };

  // Prepare chart data
  const chartData = {
    labels: emissionsHistory.map(entry => new Date(entry.timestamp)),
    datasets: [
      {
        label: 'Emissions',
        data: emissionsHistory.map(entry => entry.emissions),
        borderColor: 'rgba(6, 182, 212, 1)', // Cyan
        backgroundColor: 'rgba(6, 182, 212, 0.2)',
        fill: true,
        tension: 0.4,
        borderWidth: 2,
      }
    ]
  };

  // Chart options
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        display: false
      },
      y: {
        display: false,
        beginAtZero: false
      }
    },
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        enabled: false
      }
    },
    elements: {
      point: {
        radius: 0
      }
    }
  };

  if (loading) {
    return (
      <div className="bg-black/20 backdrop-blur-sm rounded-lg p-2 md:p-3 border border-white/5 hover:bg-black/30 transition-colors flex flex-col">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-700/50 rounded w-20 mb-2"></div>
          <div className="h-6 bg-gray-700/50 rounded w-24 mb-2"></div>
          <div className="h-[80px] bg-gray-700/50 rounded"></div>
        </div>
      </div>
    );
  }

  if (error || !emissionsData) {
    return (
      <div className="bg-black/20 backdrop-blur-sm rounded-lg p-2 md:p-3 border border-white/5 hover:bg-black/30 transition-colors flex flex-col">
        <div className="mb-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium">Emissions</span>
            <span className="text-xs text-gray-400">N/A</span>
          </div>
          <div className="flex flex-col md:flex-row md:items-center mt-1">
            <span className="text-sm md:text-base font-bold">No Data</span>
          </div>
        </div>
        <div className="h-[80px] flex items-center justify-center">
          <span className="text-xs text-gray-400">{error || 'No emissions data'}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-black/20 backdrop-blur-sm rounded-lg p-2 md:p-3 border border-white/5 hover:bg-black/30 transition-colors flex flex-col">
      <div className="mb-2">
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium">Emissions</span>
          <span className="text-xs text-gray-400">Block {emissionsData.block_number}</span>
        </div>
        <div className="flex flex-col md:flex-row md:items-center mt-1">
          <span className="text-sm md:text-base font-bold">{formatEmissions(emissionsData.emissions)}</span>
        </div>
      </div>
      <div className="h-[80px]">
        {emissionsHistory.length > 0 && (
          <Line data={chartData} options={chartOptions} />
        )}
      </div>
    </div>
  );
}